'use client';

import React from 'react';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import BgMedia from '../backgrounds/bg_media';
import { LinkWrapper } from '../../../utils/link';

const UtilityCard = ({
  blok,
  destination,
  hotel,
  season,
  hasHorizontalScroll,
  hasSiblings,
  parentWidth,
}) => {
  /**
   * useEffect hook to handle resizing and return of the card width.
   */

  const backgroundMedia = blok.image_or_video || '';

  const hasBackgroundSlider =
    blok.background_slider && blok.background_slider.length > 0;

  const mediaParams = {
    backgroundMedia: blok.image_or_video || {},
    isLooping: blok.looping || false,
    inSlider: hasHorizontalScroll || hasBackgroundSlider || false,
    imagePosition: blok.image_position || 'center',
    parentWidth: parentWidth || blok.width,
    forceAspect: blok.image_force_aspect_ratio,
    imageId: blok.qbank_id,
  };

  const placeLastBlockToBottom = blok.last_block_against_bottom_border || false;

  const horizontalAligns = {
    rows: 'flex flex-col md:flex-row justify-between md:items-center',
    cols: 'flex flex-col',
  };

  const horizontalAlign = horizontalAligns[blok.horizontal_align || 'cols'];
  const itemsLength = blok.blocks?.length + blok.links?.length || 0;

  const linkWrapsCard = (blok.links && blok.links.length > 0) || false;

  const imageLabels = blok.labels?.filter((l) => l.type === 'image') || [];

  const labels = blok.labels?.filter((l) => l.type !== 'image') || [];

  return (
    <CardWrapper
      blok={blok}
      hasSiblings={hasSiblings}
      hasHorizontalScroll={hasHorizontalScroll}
    >
      {!hasBackgroundSlider &&
        (backgroundMedia.filename || mediaParams.imageId) && (
          <BgMedia mediaParams={mediaParams} />
        )}
      {hasBackgroundSlider &&
        blok.background_slider.map((nestedBlok) => (
          <div
            className={`flex h-full w-full relative z-10 rounded`}
            key={nestedBlok._uid}
          >
            <StoryblokComponent
              blok={nestedBlok}
              key={nestedBlok._uid}
              hotel={hotel}
              season={season}
              destination={destination}
              inSlider={true}
            />
          </div>
        ))}
      {((blok.blocks && blok.blocks.length > 0) ||
        (blok.links && blok.links.length > 0)) && (
        <div
          className={`z-20 ${
            placeLastBlockToBottom ? 'h-full' : ''
          } ${horizontalAlign} ${hasBackgroundSlider ? 'absolute' : ''}`}
          key={blok._uid + '_blok'}
        >
          {blok.blocks.map((nestedBlok, index) => {
            return (
              <div
                className={`${
                  nestedBlok.component !== 'image' ? 'px-2 md:px-4' : ''
                } ${
                  placeLastBlockToBottom && index === itemsLength - 1
                    ? 'mt-auto'
                    : ''
                } z-20`}
                key={nestedBlok._uid}
              >
                <StoryblokComponent
                  blok={nestedBlok}
                  key={nestedBlok._uid}
                  hotel={hotel}
                  season={season}
                  destination={destination}
                  isNested={true}
                  parentColor={blok.background_color}
                  linkWrapsEntireParent={linkWrapsCard}
                />
              </div>
            );
          })}
          {blok.links &&
            blok.links.map((nestedBlok, index) => {
              return (
                nestedBlok.type !== 'hidden' && (
                  <div
                    className={`${
                      nestedBlok.component !== 'image' ? 'px-2 md:px-4' : ''
                    } ${
                      placeLastBlockToBottom &&
                      index + blok.blocks?.length === itemsLength - 1
                        ? 'mt-auto'
                        : ''
                    } z-20`}
                    key={nestedBlok._uid}
                  >
                    <StoryblokComponent
                      blok={nestedBlok}
                      key={nestedBlok._uid}
                      hotel={hotel}
                      season={season}
                      destination={destination}
                      isNested={true}
                      parentColor={blok.background_color}
                      linkWrapsEntireParent={linkWrapsCard}
                    />
                  </div>
                )
              );
            })}
        </div>
      )}
      {labels && labels.length > 0 && (
        <div
          className={`${hasBackgroundSlider ? 'absolute bottom-0 w-3/4' : ''} ${
            blok.links && blok.links.length > 0 ? 'absolute bottom-0' : ''
          } flex flex-row flex-wrap z-20 py-2 md:py-4 divide-none sm:divide-solid divide-black-30 divide-x md:divide-none xl:divide-solid`}
          key={blok._uid}
        >
          {labels.map((label, index) => (
            <StoryblokComponent
              blok={label}
              key={label._uid}
              hotel={hotel}
              season={season}
              destination={destination}
              index={index}
            />
          ))}
        </div>
      )}
      {imageLabels?.length > 0 && (
        <div
          className={`absolute bottom-2 ml-1 sm:px-0 ${
            mediaParams.parentWidth === 'w-full'
              ? 'sm:top-2 sm:right-2'
              : 'lg:top-4 lg:right-4'
          }  z-20 max-w-[50%] h-fit`}
        >
          {imageLabels.map((label, index) => (
            <StoryblokComponent
              blok={label}
              key={label._uid}
              hotel={hotel}
              season={season}
              destination={destination}
              index={index}
              containerSize={mediaParams?.parentWidth}
            />
          ))}
        </div>
      )}
    </CardWrapper>
  );
};

const CardWrapper = ({ blok, hasSiblings, hasHorizontalScroll, children }) => {
  const widths =
    !hasSiblings || hasHorizontalScroll
      ? {
          'w-full': 'col-span-12',
          'w-3/4': 'col-span-12 md:col-span-9',
          'w-2/3': 'col-span-12 md:col-span-8',
          'w-1/2': 'col-span-12 md:col-span-6',
          'w-1/3': 'col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4',
          'w-1/4': 'col-span-6 md:col-span-3',
          auto: 'w-fit',
        }
      : {
          'w-full': 'col-span-12',
          'w-3/4': 'col-span-12 md:col-span-6 lg:col-span-9',
          'w-2/3': 'col-span-12 md:col-span-6 lg:col-span-8',
          'w-1/2': 'col-span-12 md:col-span-6',
          'w-1/3': 'col-span-12 md:col-span-6 lg:col-span-4',
          'w-1/4': 'col-span-6 lg:col-span-3',
          auto: 'w-fit',
        };
  const width = widths[blok.width] || 'w-full';

  const heights = {
    screen: 'h-[60vh] lg:h-[80vh]',
    auto: !hasSiblings ? 'h-max sm:h-auto' : 'h-auto',
    half: 'h-[50vh] min-h-96 max-h-180',
    square: 'aspect-square',
    video: 'aspect-square md:aspect-video',
    full: 'h-full',
    fourthree: 'aspect-square md:aspect-4/3',
  };

  const height = heights[blok.height] || 'h-auto';
  const fillRemainingSpace = blok.fill_remaining_empty_space
    ? 'h-full w-full'
    : '';

  const backgroundColors = {
    yellow: 'bg-yellow-15',
    'yellow-announcement': 'bg-yellow',
    blue: 'bg-blue-7.5',
    'blue-announcement': 'bg-blue text-white',
    'blue-secondary': 'bg-blue-15',
    green: 'bg-green-7.5',
    'green-announcement': 'bg-green text-white',
    'green-secondary': 'bg-green-15',
    orange: 'bg-orange-7.5',
    'orange-announcement': 'bg-orange text-white',
    'orange-secondary': 'bg-orange-15',
    black: 'bg-black text-white',
    transparent: 'bg-transparent',
  };

  const backgroundColor =
    backgroundColors[blok.background_color] || 'bg-transparent';

  const backgroundMedia = blok.image_or_video || '';

  const verticalAligns = {
    'items-start': 'flex-col justify-normal',
    'items-end': 'flex-col justify-end',
    'items-center': 'flex-col justify-center',
    'items-between': 'flex-col justify-between',
  };

  const verticalAlign = verticalAligns[blok.vertical_align || 'items-start'];

  if (blok.links && blok.links.length > 0) {
    return (
      <div
        className={`card ${
          backgroundMedia.filename ? 'remove-padding' : ''
        }  ${width} ${height} ${fillRemainingSpace} ${
          blok.top_margin ? 'mt-2 md:mt-4' : ''
        } ${
          !backgroundMedia.filename ? backgroundColor : ''
        } max-w-screen-xl rounded relative flex ${verticalAlign} flex-wrap`}
        {...storyblokEditable(blok)}
        key={blok._uid}
      >
        <LinkWrapper
          link={blok.links[0].link}
          className="no-underline h-full"
          trackingId={blok.links[0].tracking_id}
        >
          {children}
        </LinkWrapper>
      </div>
    );
  } else {
    return (
      <div
        className={`card ${
          backgroundMedia.filename ? 'remove-padding' : ''
        }  ${width} ${height} ${fillRemainingSpace} ${
          blok.top_margin ? 'mt-2 md:mt-4' : ''
        } ${
          !backgroundMedia.filename ? backgroundColor : ''
        } max-w-screen-xl rounded relative flex ${verticalAlign} flex-wrap`}
        {...storyblokEditable(blok)}
        key={blok._uid}
      >
        {children}
      </div>
    );
  }
};

export default UtilityCard;
